import {
  type LoadableState,
  useEffectWithController
} from '@carvana/merch-react-hooks';
import useDeepEqualMemo from '@carvana/merch-react-hooks/useDeepEqualMemo';
import { useRef } from 'react';
import type {
  ConfigurationRequest,
  ResponseForRequest
} from '~/contracts/new-cars';
import fetchNewCarConfiguration from '~/services/new-cars/fetchNewCarConfiguration';
import { generateErrorLogger } from '~/utilities/loggers';
import pickRequestProps from '~/utilities/new-cars/pickRequestProps';
import {
  type ConfigurationSortKey,
  sortConfigurationResults
} from '~/utilities/new-cars/sortConfigurationResults';

type OwnArgs<R extends ConfigurationRequest> = {
  loadableState: LoadableState<ResponseForRequest<R>>;
  request: R | null;

  /**
   * An identifier for the type of request being made. This is used for debugging purposes.
   */
  requestType: string;

  sortKey?: ConfigurationSortKey;
};

export default function useGetNewCarConfiguration<
  R extends ConfigurationRequest
>({
  loadableState,
  request,
  requestType,
  sortKey = 'lowestPrice'
}: OwnArgs<R>) {
  const isInitialRender = useRef(true);
  // Captures isLoaded at mount time. NOT in the dep array — including
  // loadableState.isLoaded caused an infinite abort/refetch cycle because
  // setLoading() sets isLoaded = false, re-triggering the effect.
  const initialIsLoadedRef = useRef(loadableState.isLoaded);

  const { setData, setLoading, setError } = loadableState;

  const requestToUse = useDeepEqualMemo(
    request ? pickRequestProps(request) : null
  );

  useEffectWithController(
    controller => {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        // Fall through when request is present but data isn't loaded yet
        // (restoration without server-prefetch). Skip when data is already loaded.
        if (!requestToUse || initialIsLoadedRef.current) return;
      }

      if (!requestToUse) return;

      setLoading();

      fetchNewCarConfiguration(requestToUse, requestType, controller)
        .then(data => ({
          ...data,
          results: sortConfigurationResults(data.results, sortKey)
        }))
        .then(setData)
        .catch(
          generateErrorLogger(
            'Get New Car Configuration',
            { identifier: requestType, request: requestToUse },
            { callback: setError, controller }
          )
        );
    },
    [requestType, requestToUse, setData, setError, setLoading, sortKey]
  );
}
